import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import styles from '../PdfRenderer.module.scss';

export interface DropdownOption<T extends string = string> {
    label: string;
    value: T;
    icon?: string;
}

interface Props<T extends string = string> {
    options: DropdownOption<T>[];
    value: T;
    onChange: (value: T) => void;
    placeholder?: string;
    icon?: string;
    className?: string;
}

export function CustomDropdown<T extends string = string>({
    options,
    value,
    onChange,
    placeholder = '',
    icon,
    className,
}: Props<T>) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const selected = options.find(o => o.value === value);

    return (
        <div ref={ref} className={clsx(styles.filterDropdown, className, open && styles['filterDropdown--open'])}>
            <button
                type="button"
                className={styles.filterDropdownTrigger}
                onClick={() => setOpen(v => !v)}
            >
                {icon && <i className={icon} />}
                <span className={styles.filterDropdownLabel}>
                    {selected?.label ?? placeholder}
                </span>
                <i className={clsx('fa-solid', open ? 'fa-chevron-up' : 'fa-chevron-down', styles.filterDropdownChevron)} />
            </button>
            {open && (
                <div className={styles.filterDropdownMenu}>
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            className={clsx(
                                styles.filterDropdownItem,
                                opt.value === value && styles['filterDropdownItem--active'],
                            )}
                            onClick={() => {
                                onChange(opt.value);
                                setOpen(false);
                            }}
                        >
                            {opt.icon && <i className={opt.icon} />}
                            <span>{opt.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
